/**
 * Feedback Loop Layer Assignment
 * 
 * Processes feedback loops level by level (level 1, then 2, etc.) and assigns
 * Y-layer positions to feedback nodes. Feedback nodes are NOT placed into the
 * regular layer array — they get fractional Y positions between critical path layers.
 * 
 * For each back-edge at a given nesting level:
 *   1. Trace backwards from the back-edge source to collect all loop nodes
 *   2. Topologically sort them within the loop
 *   3. Distribute Y positions between the back-edge target and entry point
 */

/**
 * Trace backwards from a back-edge source to find all feedback nodes in a loop.
 * 
 * @param {string} backEdgeSourceId - The node the back-edge originates from
 * @param {number} level - The nesting level to match
 * @param {Map<string, number>} nodeLevels - Map of nodeId -> nesting level
 * @param {Object} graph - Dependency graph
 * @param {Set<string>} assignedToLoop - Nodes already assigned to a loop (mutated)
 * @returns {string[]} Array of node IDs in this loop
 */
function traceLoopNodes(backEdgeSourceId, level, nodeLevels, graph, assignedToLoop) {
  const loopNodes = [];
  const queue = [backEdgeSourceId];
  const visited = new Set();
  
  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    
    // Only include feedback nodes at THIS level
    const nodeLevel = nodeLevels.get(nodeId);
    if (nodeLevel === level && !assignedToLoop.has(nodeId)) {
      loopNodes.push(nodeId);
      assignedToLoop.add(nodeId);
    }
    
    // Trace to forward parents (going backwards in the chain)
    const parents = graph.parents.get(nodeId) || [];
    for (const parentId of parents) {
      const parentLevel = nodeLevels.get(parentId);
      if (parentLevel === level && !visited.has(parentId)) {
        queue.push(parentId);
      }
    }
  }
  
  return loopNodes;
}

/**
 * Topologically sort nodes within a loop using Kahn's algorithm.
 * Returns nodes in reversed order (back-edge source at top, entry at bottom)
 * for upward-flow feedback rendering.
 * 
 * @param {string[]} loopNodes - Node IDs in this loop
 * @param {Object[]} feedbackTasks - Task objects for feedback nodes
 * @param {Object} graph - Dependency graph
 * @returns {Object[]} Ordered task objects (top-to-bottom for rendering)
 */
function topoSortLoopNodes(loopNodes, feedbackTasks, graph) {
  const loopNodeSet = new Set(loopNodes);
  const loopTasks = loopNodes
    .map(id => feedbackTasks.find(t => t.id === id))
    .filter(Boolean);
  
  // Build in-degree map for nodes within this loop only
  const inDegree = new Map();
  for (const task of loopTasks) {
    const parents = graph.allParents.get(task.id) || [];
    const loopParents = parents.filter(pid => loopNodeSet.has(pid));
    inDegree.set(task.id, loopParents.length);
  }
  
  // Kahn's algorithm
  const orderedLoopNodes = [];
  const topoQueue = loopTasks.filter(t => inDegree.get(t.id) === 0);
  
  while (topoQueue.length > 0) {
    const node = topoQueue.shift();
    orderedLoopNodes.push(node);
    
    // Reduce in-degree of children within this loop
    const children = graph.allChildren.get(node.id) || [];
    for (const childId of children) {
      if (loopNodeSet.has(childId)) {
        const newDegree = inDegree.get(childId) - 1;
        inDegree.set(childId, newDegree);
        if (newDegree === 0) {
          const childTask = loopTasks.find(t => t.id === childId);
          if (childTask) topoQueue.push(childTask);
        }
      }
    }
  }
  
  // Add any remaining nodes (in case of cycles within the loop)
  for (const task of loopTasks) {
    if (!orderedLoopNodes.includes(task)) {
      orderedLoopNodes.push(task);
    }
  }
  
  // Reverse for upward flow (back-edge source at top, entry at bottom)
  orderedLoopNodes.reverse();
  return orderedLoopNodes;
}

/**
 * Find the entry parent layer for a loop at a given nesting level.
 * 
 * For level 1: the reference is the critical path (taskLayers).
 * For level N: the reference includes lower-level feedback nodes (feedbackLayerInfo).
 * 
 * @param {string[]} loopNodes - Node IDs in this loop
 * @param {number} level - Nesting level
 * @param {string} backEdgeTargetId - Where the back-edge points to
 * @param {Map<string, number>} taskLayers - Critical path layer assignments
 * @param {Map<string, Object>} feedbackLayerInfo - Already-assigned feedback positions
 * @param {Map<string, number>} nodeLevels - Node nesting levels
 * @param {Set<string>} feedbackNodes - All feedback node IDs
 * @param {Object} graph - Dependency graph
 * @returns {{ backEdgeTargetLayer: number, entryParentLayer: number }}
 */
function findEntryLayers(loopNodes, level, backEdgeTargetId, taskLayers, feedbackLayerInfo, nodeLevels, feedbackNodes, graph) {
  let backEdgeTargetLayer;
  let entryParentLayer;
  
  if (level === 1) {
    // Reference is critical path
    backEdgeTargetLayer = taskLayers.get(backEdgeTargetId) || 0;
    
    // Find entry point (critical path node that leads into this loop)
    entryParentLayer = backEdgeTargetLayer;
    
    for (const nodeId of loopNodes) {
      const allParents = graph.allParents.get(nodeId) || [];
      for (const parentId of allParents) {
        // The entry parent is a critical path node (not a feedback node)
        if (!feedbackNodes.has(parentId)) {
          const pLayer = taskLayers.get(parentId);
          if (pLayer !== undefined && pLayer > entryParentLayer) {
            entryParentLayer = pLayer;
          }
        }
      }
    }
  } else {
    // Reference is previous level feedback nodes
    if (taskLayers.has(backEdgeTargetId)) {
      backEdgeTargetLayer = taskLayers.get(backEdgeTargetId);
    } else if (feedbackLayerInfo.has(backEdgeTargetId)) {
      backEdgeTargetLayer = feedbackLayerInfo.get(backEdgeTargetId).yLayer;
    } else {
      backEdgeTargetLayer = 0;
    }
    
    // Find entry point from previous level
    entryParentLayer = backEdgeTargetLayer;
    
    for (const nodeId of loopNodes) {
      const allParents = graph.allParents.get(nodeId) || [];
      for (const parentId of allParents) {
        const parentLevel = nodeLevels.get(parentId) || 0;
        if (parentLevel < level) {
          let pLayer;
          if (taskLayers.has(parentId)) {
            pLayer = taskLayers.get(parentId);
          } else if (feedbackLayerInfo.has(parentId)) {
            pLayer = feedbackLayerInfo.get(parentId).yLayer;
          }
          if (pLayer !== undefined && pLayer > entryParentLayer) {
            entryParentLayer = pLayer;
          }
        }
      }
    }
  }
  
  return { backEdgeTargetLayer, entryParentLayer };
}

/**
 * Calculate Y-layer positions for ordered feedback nodes within a loop.
 * Nodes are distributed between the back-edge target layer and the entry layer.
 * 
 * @param {Object[]} orderedNodes - Topologically sorted task objects
 * @param {number} backEdgeTargetLayer - Layer of the back-edge target
 * @param {number} entryParentLayer - Layer of the entry parent
 * @param {number} lastCriticalLayer - Index of the last critical path layer
 * @param {string} backEdgeTargetId - ID of the back-edge target node
 * @param {number} level - Nesting level
 * @param {number} loopIndex - Index of this loop
 * @param {Map<string, Object>} feedbackLayerInfo - Map to populate (mutated)
 * @param {Map<string, number>} taskLayers - Map to populate (mutated)
 */
function assignFeedbackYPositions(orderedNodes, backEdgeTargetLayer, entryParentLayer, lastCriticalLayer, backEdgeTargetId, level, loopIndex, feedbackLayerInfo, taskLayers) {
  const numNodes = orderedNodes.length;
  
  // Available space between target and entry (with padding)
  const padding = 0.3;
  let startLayer = backEdgeTargetLayer + padding;
  let endLayer = entryParentLayer - padding;
  let availableSpace = endLayer - startLayer;
  
  // SPECIAL CASE: When the available space is insufficient, place feedback
  // nodes BELOW the entry point. Each node gets its own layer slot.
  const minSpaceNeeded = numNodes <= 1 ? 0.1 : (numNodes - 1) * 0.3;
  if (availableSpace < minSpaceNeeded) {
    startLayer = Math.max(entryParentLayer, lastCriticalLayer) + 0.5;
    endLayer = startLayer + numNodes;
    availableSpace = endLayer - startLayer;
  }
  
  for (let i = 0; i < orderedNodes.length; i++) {
    const feedbackTask = orderedNodes[i];
    
    let feedbackYLayer;
    if (numNodes === 1) {
      // Single node: place in the middle of available space
      feedbackYLayer = startLayer + availableSpace / 2;
    } else {
      // Multiple nodes: distribute evenly between start and end
      const fraction = i / (numNodes - 1);
      feedbackYLayer = startLayer + fraction * availableSpace;
    }
    
    feedbackLayerInfo.set(feedbackTask.id, {
      yLayer: feedbackYLayer,
      targetId: backEdgeTargetId,
      targetLayer: backEdgeTargetLayer,
      parentLayer: entryParentLayer,
      orderIndex: i,
      totalFeedbackNodes: numNodes,
      loopIndex: loopIndex,
      nestingLevel: level,
    });
    
    taskLayers.set(feedbackTask.id, feedbackYLayer);
  }
}

/**
 * Process all feedback loops level by level and assign Y positions.
 * 
 * @param {Object[]} sortedTasks - All tasks sorted by ID
 * @param {string[][]} layers - Critical path layers
 * @param {Map<string, number>} taskLayers - Layer assignments (mutated)
 * @param {Object} graph - Dependency graph
 * @param {Set<string>} feedbackNodes - Set of feedback node IDs
 * @param {Map<string, number>} nodeLevels - Map of nodeId -> nesting level
 * @returns {Map<string, Object>} feedbackLayerInfo - Feedback node positions
 */
export function assignFeedbackLayers(sortedTasks, layers, taskLayers, graph, feedbackNodes, nodeLevels) {
  const feedbackLayerInfo = new Map();
  const feedbackTasksRemaining = sortedTasks.filter(t => feedbackNodes.has(t.id));
  
  if (feedbackTasksRemaining.length === 0 || !graph.backEdgesWithLevels || graph.backEdgesWithLevels.length === 0) {
    return feedbackLayerInfo;
  }
  
  // Find the maximum nesting level
  const maxLevel = Math.max(...graph.backEdgesWithLevels.map(e => e.level));
  
  // Process each level in order
  for (let level = 1; level <= maxLevel; level++) {
    const levelBackEdges = graph.backEdgesWithLevels.filter(e => e.level === level);
    if (levelBackEdges.length === 0) continue;
    
    // Group feedback nodes by which back-edge they belong to
    const assignedToLoop = new Set();
    let loopIndex = feedbackLayerInfo.size;
    
    for (const backEdge of levelBackEdges) {
      loopIndex++;
      
      // 1. Trace loop nodes
      const loopNodes = traceLoopNodes(backEdge.from, level, nodeLevels, graph, assignedToLoop);
      if (loopNodes.length === 0) continue;
      
      // Debug logging
      const loopNodeSet = new Set(loopNodes);
      const loopTasks_debug = loopNodes.map(id => feedbackTasksRemaining.find(t => t.id === id)).filter(Boolean);
      const inDegrees_debug = loopTasks_debug.map(task => {
        const parents = graph.allParents.get(task.id) || [];
        const loopParents = parents.filter(pid => loopNodeSet.has(pid));
        return `${task.id.slice(-8)}(${loopParents.length})`;
      });
      console.log('[LayerAssignment] Loop', loopIndex, '- nodes with in-degrees:', inDegrees_debug.join(', '));
      
      // 2. Find entry layers
      const { backEdgeTargetLayer, entryParentLayer } = findEntryLayers(
        loopNodes, level, backEdge.to,
        taskLayers, feedbackLayerInfo, nodeLevels, feedbackNodes, graph
      );
      
      // 3. Topological sort
      const orderedLoopNodes = topoSortLoopNodes(loopNodes, feedbackTasksRemaining, graph);
      
      // Debug: Show final order
      console.log('[LayerAssignment] Loop', loopIndex, '- final order (top→bottom):', 
        orderedLoopNodes.map(t => t.id.slice(-8)).join(' → '));
      
      // 4. Assign Y positions
      assignFeedbackYPositions(
        orderedLoopNodes, backEdgeTargetLayer, entryParentLayer,
        layers.length - 1, backEdge.to, level, loopIndex,
        feedbackLayerInfo, taskLayers
      );
    }
  }
  
  return feedbackLayerInfo;
}
